import type { BoardActivity, BoardEmployee } from './plantafelTypes';

export function localDayKeyFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function activityOnDay(activity: BoardActivity, dayKey: string): boolean {
  if (!activity.due_at) return false;
  const start = new Date(activity.due_at);
  if (Number.isNaN(start.getTime())) return false;
  // Mirror the server overlap semantics (plantafel.ts activityOverlapsRange):
  // fall back to a 1h window when no end is set, and treat the activity as
  // belonging to every calendar day its [start, end] interval overlaps.
  const end = activity.end_at ? new Date(activity.end_at) : new Date(start.getTime() + 3600000);
  if (Number.isNaN(end.getTime())) return false;
  const [y, m, d] = dayKey.split('-').map(Number);
  if (!y || !m || !d) return false;
  const dayStart = new Date(y, m - 1, d, 0, 0, 0, 0);
  const dayEnd = new Date(y, m - 1, d, 23, 59, 59, 999);
  return start <= dayEnd && end >= dayStart;
}

export function assignedToEmployee(activity: BoardActivity, employeeId: string): boolean {
  if (activity.primary_employee_id === employeeId) return true;
  return activity.assignee_employees.some((e) => e.id === employeeId);
}

export function primaryEmployeeForActivity(
  activity: BoardActivity,
  employees: BoardEmployee[],
): BoardEmployee | null {
  const primaryId =
    activity.primary_employee_id ||
    activity.assignee_employees[0]?.id ||
    null;
  if (!primaryId) return null;
  return employees.find((e) => e.id === primaryId) ?? null;
}

export function extraAssigneeCount(activity: BoardActivity): number {
  const ids = new Set(activity.assignee_employees.map((e) => e.id));
  return Math.max(0, ids.size - 1);
}

export type MappableActivity = BoardActivity & {
  lead_site_latitude: number;
  lead_site_longitude: number;
};

export function isMappableActivity(activity: BoardActivity): activity is MappableActivity {
  const lat = activity.lead_site_latitude;
  const lng = activity.lead_site_longitude;
  return lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);
}

export function groupStopsByEmployee(
  activities: MappableActivity[],
  employees: BoardEmployee[],
): Array<{ employee: BoardEmployee; stops: MappableActivity[] }> {
  const result: Array<{ employee: BoardEmployee; stops: MappableActivity[] }> = [];
  for (const employee of employees) {
    const stops = activities
      .filter((a) => assignedToEmployee(a, employee.id))
      .sort((a, b) => new Date(a.due_at!).getTime() - new Date(b.due_at!).getTime());
    if (stops.length > 0) result.push({ employee, stops });
  }
  return result;
}

export async function fetchDrivingRoute(
  stops: Array<{ lat: number; lng: number }>,
): Promise<[number, number][] | null> {
  if (stops.length < 2) return null;
  const points = stops.map((s) => `${s.lng},${s.lat}`).join(';');
  try {
    const res = await fetch(`/api/routing/driving?points=${encodeURIComponent(points)}`);
    if (!res.ok) return null;
    const data = (await res.json()) as { coordinates?: [number, number][] };
    return Array.isArray(data.coordinates) ? data.coordinates : null;
  } catch {
    return null;
  }
}

export type EmployeeRoute = {
  employeeId: string;
  color: string;
  coordinates: [number, number][];
};

export async function fetchEmployeeRoutes(
  groups: Array<{ employee: BoardEmployee; stops: MappableActivity[] }>,
): Promise<EmployeeRoute[]> {
  const routes: EmployeeRoute[] = [];
  await Promise.all(
    groups.map(async ({ employee, stops }) => {
      if (stops.length < 2) return;
      const coords = await fetchDrivingRoute(
        stops.map((s) => ({ lat: s.lead_site_latitude, lng: s.lead_site_longitude })),
      );
      if (coords && coords.length >= 2) {
        routes.push({ employeeId: employee.id, color: employee.color, coordinates: coords });
      }
    }),
  );
  return routes;
}
